/** Multi-account broker view-model for the frontend.
 *
 * Backend today supports a SINGLE Zerodha session — there's no
 * `broker_accounts` table, no per-account session store. This module
 * exposes a `BrokerAccount` shape that *would* come from that future
 * endpoint, and an adapter that maps the current single-session
 * `/api/broker/kite/status` response into a list of one account.
 *
 * When the backend grows multi-account support, this hook flips from a
 * synthesised list to a fetched list — call sites stay the same.
 */

import type { DhanStatus } from "@/lib/dhanApi";
import type { KitePosition, KiteStatus } from "@/types/broker";

export type BrokerName = "Zerodha" | "Fyers" | "Dhan" | "Upstox";

export type AccountStatus = "connected" | "disconnected";

export interface BrokerAccount {
  /** Stable id — for the synthesised account we use a constant string. */
  id: string;
  broker: BrokerName;
  /** Human-friendly label ("Main", "HUF", "Spouse"). */
  label: string;
  status: AccountStatus;
  /** Broker user id ("KG6424") when known. */
  userId: string | null;
  /** Full display name from broker if available. */
  displayName: string | null;
  /** Issued-at ISO timestamp when connected, else null. */
  issuedAt: string | null;
  /** When the token dies, if the broker says (Dhan); null = the daily 06:00 IST kill (Kite, Fyers). */
  expiresAt: string | null;
  /** Positions count for this account (0 when disconnected). */
  openPositionCount: number;
  /** Strategies that route entries to this account. Empty when unassigned. */
  assignedStrategies: string[];
}

/** Adapter for the single-session backend we have today. Returns
 * always-one account labelled "Main"; status reflects the real Kite
 * session. Positions come from the existing positions endpoint so the
 * row's open-trade pill is honest. */
export function buildSingleAccount(
  status: KiteStatus,
  positions: KitePosition[],
  assignedStrategies: string[],
): BrokerAccount {
  return {
    id: "zerodha-main",
    broker: "Zerodha",
    label: "Main",
    status: status.connected ? "connected" : "disconnected",
    userId: status.user_id,
    displayName: null,
    issuedAt: status.issued_at,
    expiresAt: null,
    openPositionCount: countOpen(positions),
    assignedStrategies,
  };
}

/** Adapter for the single Fyers session (ST-5). Fyers has no positions
 * endpoint yet, so the open-position count is always 0 — the row shows
 * "Idle" while connected. Same daily-token model as Kite. */
export function buildFyersAccount(status: KiteStatus, assignedStrategies: string[]): BrokerAccount {
  return {
    id: "fyers-main",
    broker: "Fyers",
    label: "Main",
    status: status.connected ? "connected" : "disconnected",
    userId: status.user_id,
    displayName: null,
    issuedAt: status.issued_at,
    expiresAt: null,
    openPositionCount: 0,
    assignedStrategies,
  };
}

/** Adapter for the Dhan session: a pasted 24-hour token that a daily job renews. The expiry comes
 * from Dhan, not from the 06:00 IST rule. Dhan has no positions read here yet, so 0 open. */
export function buildDhanAccount(status: DhanStatus, assignedStrategies: string[]): BrokerAccount {
  return {
    id: "dhan-main",
    broker: "Dhan",
    label: "Main",
    status: status.connected ? "connected" : "disconnected",
    userId: status.user_id,
    displayName: null,
    issuedAt: status.issued_at,
    expiresAt: status.expires_at,
    openPositionCount: 0,
    assignedStrategies,
  };
}

function countOpen(positions: KitePosition[]): number {
  let count = 0;
  for (const p of positions) {
    if (p.quantity !== 0) count++;
  }
  return count;
}

/** Hours-and-minutes string describing time until next 06:00 IST token
 * expiry, given the current time. Examples: "15h 38m", "expired".
 *
 * Kite tokens are minted on login and die at 06:00 IST regardless. So
 * "expires in" is just (next 6 AM) - now. Caller passes `now` to keep
 * this function pure & testable. */
export function expiresInLabel(now: Date): string {
  const target = nextSixAmIst(now);
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return "expired";
  const totalMins = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${String(mins)}m`;
  return `${String(hours)}h ${String(mins).padStart(2, "0")}m`;
}

/** Hours-and-minutes until an ISO timestamp, e.g. "21h 05m", or "expired". Pure: `now` is passed in. */
export function untilLabel(iso: string, now: Date): string {
  const ms = new Date(iso).getTime() - now.getTime();
  if (Number.isNaN(ms) || ms <= 0) return "expired";
  const totalMins = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${String(mins)}m`;
  return `${String(hours)}h ${String(mins).padStart(2, "0")}m`;
}

function nextSixAmIst(now: Date): Date {
  // Local approximation — assumes the server is in IST. For correctness
  // we'd use Intl.DateTimeFormat with timeZone:"Asia/Kolkata" but for
  // a single-user single-VPS deploy this is fine.
  const t = new Date(now);
  t.setHours(6, 0, 0, 0);
  if (t.getTime() <= now.getTime()) {
    // Already past 6 AM today — target is tomorrow's 6 AM.
    t.setDate(t.getDate() + 1);
  }
  return t;
}
